import { AppIcon, useImageLoad } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { DailyResource } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/dailyResource';
import { resourceImageSrc } from '@backrs_ui/grow-ui/src/pages/SatActBooster/utils/resourceImage';

import { Box, Divider, Skeleton, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.dailyPractice.resource;

type ResourceCardProps = Readonly<{
  resource: DailyResource;
}>;

/**
 * The day's resource card: a Khan skill unit on study days, or the official full-length section on
 * timed days. The whole card opens the resource in a new tab. The artwork is the per-category (or
 * per-test) image; the skeleton holds the space until it decodes, and stays if no image is mapped.
 */
export const ResourceCard = ({ resource }: ResourceCardProps) => {
  const isPracticeTest = resource.kind === 'practiceTest';

  const fallbackTitle = isPracticeTest
    ? STRINGS.practiceTestTitle(resource.section)
    : STRINGS.khanTitle(resource.category);
  const title = resource.label ?? fallbackTitle;

  const timing = isPracticeTest ? resource.timing : null;

  const description = timing
    ? STRINGS.timedBlurb(resource.test.toUpperCase(), timing.minutes, timing.questionCount)
    : isPracticeTest
      ? STRINGS.practiceTestBlurb
      : resource.skillText;
  const note = resource.note;

  const imageSrc = resourceImageSrc(resource);
  const image = useImageLoad(imageSrc ?? undefined);

  return (
    <Box
      component="a"
      id="booster-resource-card"
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={STRINGS.openAria}
      sx={cardSx}
    >
      <Box sx={headerSx}>
        <Box sx={textColSx}>
          <Typography variant="text-lg-semibold" sx={titleSx}>
            {title}
          </Typography>
          {description && (
            <Typography variant="text-sm-regular" sx={descriptionSx}>
              {description}
            </Typography>
          )}
          {note && (
            <Typography variant="text-xs-regular" sx={noteSx}>
              {note}
            </Typography>
          )}
        </Box>
        <Box sx={openButtonSx} aria-hidden>
          <AppIcon iconName="ArrowUpRight" diameter="20px" color="Colors/Text/text-secondary" />
        </Box>
      </Box>

      {/* Full-width separator between the header and the artwork. */}
      <Divider sx={dividerSx} />

      <Box sx={thumbnailSx} aria-hidden>
        {!image.imageLoaded && (
          <Skeleton variant="rectangular" animation="wave" sx={thumbnailSkeletonSx} />
        )}
        {imageSrc && (
          <Box
            component="img"
            src={imageSrc}
            alt=""
            sx={thumbnailImageSx(image.imageLoaded)}
            {...image.imageProps}
          />
        )}
      </Box>
    </Box>
  );
};

const cardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  textDecoration: 'none',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-quinary']}`,
  borderRadius: backrs.radius['radius-xl'],
  transition: 'border-color 0.2s ease-in-out',
  '&:hover': {
    borderColor: backrs.colors['Colors/Border/border-brand'],
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-xl'],
  paddingTop: backrs.spacing['spacing-2xl'],
  paddingBottom: backrs.spacing['spacing-2xl'],
  paddingInline: backrs.spacing['spacing-3xl'],
});

const textColSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  minWidth: 0,
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const noteSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
  fontStyle: 'italic',
});

const openButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

const dividerSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-secondary'],
});

const thumbnailSx: SxProps<Theme> = {
  position: 'relative',
  height: '200px',
  overflow: 'hidden',
};

const thumbnailSkeletonSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const thumbnailImageSx = (loaded: boolean): SxProps<Theme> => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
});
